import { useEffect, useRef, useState, useCallback } from 'react';
import * as ort from 'onnxruntime-web';
import { preprocess, postprocess } from '../lib/imageProcessing';

// onnxruntime-web 1.21.0 — tested version for this model
// WASM files are fetched from CDN (no local copy needed)
const ORT_CDN = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/';
const CACHE_NAME = 'psw-models-v1';

/**
 * Fetch model bytes, serving from Cache API when available.
 * onProgress(0-1) is called during network download.
 */
async function fetchWithCache(url, onProgress) {
  // 1. Try cache first
  if ('caches' in window) {
    try {
      const cache  = await caches.open(CACHE_NAME);
      const cached = await cache.match(url);
      if (cached) {
        onProgress?.(1);
        return await cached.arrayBuffer();
      }
    } catch (e) {
      console.warn('[useYoloModel] cache read error:', e.message);
    }
  }

  // 2. Network fetch with streaming progress
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} loading ${url}`);

  const contentLength = res.headers.get('Content-Length');
  const total = contentLength ? parseInt(contentLength, 10) : null;

  let buf;
  if (res.body && total) {
    const reader = res.body.getReader();
    const bytes  = new Uint8Array(total);
    let offset   = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes.set(value, offset);
      offset += value.length;
      onProgress?.(offset / total);
    }
    buf = bytes.buffer;
  } else {
    // Fallback: no progress tracking
    buf = await res.arrayBuffer();
    onProgress?.(1);
  }

  // 3. Store in cache for future visits
  if ('caches' in window) {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(url, new Response(buf.slice(0), {
        headers: { 'Content-Type': 'application/octet-stream' },
      }));
    } catch (e) {
      console.warn('[useYoloModel] cache write error:', e.message);
    }
  }

  return buf;
}

// backend string → ort execution providers list
// Mirrors the HTML reference: each option falls back to wasm
export function backendToProviders(backend) {
  switch (backend) {
    case 'webgpu': return ['webgpu', 'wasm'];
    case 'webgl':  return ['webgl',  'wasm'];
    case 'wasm':   return ['wasm'];
    default:       return ['wasm']; // 'auto' or unknown → wasm (fastest for patched INT8 model)
  }
}

export function useYoloModel({ modelUrl = '/model.onnx', executionProviders = ['wasm'] } = {}) {
  const sessionRef = useRef(null);
  const [state, setState] = useState({
    loading: true,
    ready: false,
    error: null,
    backend: null,
    phase: 'download',   // 'download' | 'init'
    progress: 0,         // 0-1 download fraction
  });

  useEffect(() => {
    let cancelled = false;
    sessionRef.current = null;
    setState({ loading: true, ready: false, error: null, backend: null, phase: 'download', progress: 0 });

    async function init() {
      try {
        ort.env.wasm.wasmPaths = ORT_CDN;
        ort.env.wasm.numThreads = navigator.hardwareConcurrency || 4;
        if (ort.env.webgl) ort.env.webgl.pack = false;

        const buf = await fetchWithCache(modelUrl, p => {
          if (!cancelled) setState(s => ({ ...s, phase: 'download', progress: p }));
        });
        if (cancelled) return;

        setState(s => ({ ...s, phase: 'init', progress: 1 }));

        let usedBackend = 'wasm';
        for (const ep of executionProviders) {
          try {
            const sess = await ort.InferenceSession.create(buf, { executionProviders: [ep] });
            if (cancelled) { sess.release?.(); return; }
            sessionRef.current = sess;
            usedBackend = ep;
            break;
          } catch (e) {
            console.warn(`[useYoloModel] backend "${ep}" unavailable:`, e.message);
          }
        }

        if (!cancelled) {
          setState({ loading: false, ready: true, error: null, backend: usedBackend, phase: 'ready', progress: 1 });
        }
      } catch (e) {
        if (!cancelled) {
          setState({ loading: false, ready: false, error: e.message, backend: null, phase: 'error', progress: 0 });
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [modelUrl, executionProviders.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  const runInference = useCallback(
    async (imgElement, { confThresh = 0.25, iouThresh = 0.45 } = {}) => {
      const sess = sessionRef.current;
      if (!sess) throw new Error('Model not ready');

      const t0 = performance.now();
      const { tensor, scale, ox, oy } = preprocess(imgElement);
      const tPre = performance.now() - t0;

      const t1 = performance.now();
      const feeds = {
        [sess.inputNames[0]]: new ort.Tensor('float32', tensor, [1, 3, 512, 512]),
      };
      const out = await sess.run(feeds);
      const tInf = performance.now() - t1;

      const t2 = performance.now();
      const dets = postprocess(
        out[sess.outputNames[0]],
        out[sess.outputNames[1]],
        confThresh,
        iouThresh,
      );
      const tPost = performance.now() - t2;

      return {
        detections: dets,
        scale,
        ox,
        oy,
        perf: { preprocess: tPre, inference: tInf, postprocess: tPost },
      };
    },
    [],
  );

  return { ...state, runInference };
  // Exposed: loading, ready, error, backend, phase, progress, runInference
}
